// Build.job is only set where the legacy jobUidString was already a SQL Job id. Older Builds hold a
// legacy Firestore id instead, which matches the Job's uidString.
export interface BuildJobRef {
  job?: { id: string } | null;
  jobUidString?: string | null;
  jobNumberString?: string | null;
}

export interface BuildJobLookups {
  jobIdByUidString?: ReadonlyMap<string, string>;
  jobIdByJobNumber?: ReadonlyMap<string, string>;
}

export const STAGED_BUILDS_JOB_UID = "staged_builds";

const SQL_ID_PATTERN = /^[0-9a-f]{32}$/;

export function normalizeJobId(value: string): string {
  return value.trim().replaceAll("-", "").toLowerCase();
}

export function isSqlJobId(value: string | null | undefined): value is string {
  return !!value && SQL_ID_PATTERN.test(normalizeJobId(value));
}

// The SQL Job id a Build can name without a lookup: its Build.job FK, else a jobUidString that's a SQL id.
export function directBuildJobId(build: BuildJobRef): string | null {
  if (build.job?.id) return normalizeJobId(build.job.id);
  return isSqlJobId(build.jobUidString) ? normalizeJobId(build.jobUidString) : null;
}

// The legacy Firestore id to look up through Job.uidString, when the Build can't name its Job directly.
export function legacyBuildJobUid(build: BuildJobRef): string | null {
  if (directBuildJobId(build)) return null;
  const uid = build.jobUidString?.trim();
  return uid && uid !== STAGED_BUILDS_JOB_UID ? uid : null;
}

// Build.job, then a SQL id in jobUidString, then Job.uidString, then jobNumber as a last resort.
export function resolveBuildJobId(build: BuildJobRef, lookups: BuildJobLookups = {}): string | null {
  if (build.jobUidString?.trim() === STAGED_BUILDS_JOB_UID && !build.job?.id) return null;
  const direct = directBuildJobId(build);
  if (direct) return direct;
  const uid = legacyBuildJobUid(build);
  const byUid = uid ? lookups.jobIdByUidString?.get(uid) : undefined;
  if (byUid) return normalizeJobId(byUid);
  const byNumber = build.jobNumberString ? lookups.jobIdByJobNumber?.get(build.jobNumberString) : undefined;
  return byNumber ? normalizeJobId(byNumber) : null;
}
